import React from 'react'
import styled from '@emotion/styled'
import { noop, isFunction } from 'lodash-es'
import { ICellRendererParams, IRowNode } from 'ag-grid-community'
import { Tooltip } from 'view/components/tooltip'
import { Locale, Translation } from 'locale'
import { chevronCircleRight } from '../styles/gridIconSet'
import * as constants from 'view/styles/constants'

type ColumnIconGetter = (groupLabel: string, leafChildren: IRowNode[], params: GroupCellRendererProps) => JSX.Element

interface GroupCellRendererProps extends ICellRendererParams {
  isGroupByLevel?: boolean
  columnKeyFormatters?: { [columnId: string]: (key: string) => string }
  columnIcons?: { [columnId: string]: JSX.Element | ColumnIconGetter }
  columnLabelRenderers?: { [columnId: string]: (label: string, key: IRowNode[]) => JSX.Element }
}

export const Container = styled.div<{ nestingLevel: number; isGroup: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: ${props => props.nestingLevel * 20 + 15}px;
`

export const GroupIcon = styled.button<{ isExpanded: boolean }>`
  transform: rotate(${props => (props.isExpanded ? 90 : 0)}deg);
  width: 16px;
  height: 16px;
  cursor: pointer;

  svg {
    color: ${constants.CORE_PRIMARY_5};
    width: 16px;
    height: 16px;
  }
`

export const LabelWrapper = styled.div`
  margin-left: 10px;
  font-weight: ${constants.FontWeight.Bold};
  color: ${constants.CORE_PRIMARY_6};
`

export const GroupCellRenderer: React.FunctionComponent<GroupCellRendererProps> = props => {
  const { node, isGroupByLevel, columnKeyFormatters, columnLabelRenderers, columnIcons, context } = props

  const handleClick = () => {
    node.setExpanded(!node.expanded)
    context.autoSizeGroupColumn()
  }

  const getHighestChildLevel = (leafChildren: IRowNode[]) => {
    return leafChildren.reduce((acc, leafChild) => (leafChild.level > acc ? leafChild.level : acc), 0)
  }

  const getLabelFromKey = (nodeKey: string): string => {
    const formatKey = rowGroupColId && columnKeyFormatters?.[rowGroupColId]

    if (formatKey) {
      return formatKey ? formatKey(nodeKey) || nodeKey : nodeKey
    } else {
      const splitKey = nodeKey.split(/_(.+)/).filter(Boolean)
      if (splitKey.length === 2) {
        const formatter = columnKeyFormatters?.[splitKey[0]]
        return formatter ? formatter(splitKey[1]) || nodeKey : nodeKey
      }
    }
    return nodeKey
  }

  const rowGroupColId = node.rowGroupColumn?.getColId()
  const isGroup = isGroupByLevel && node.allLeafChildren ? node.level < getHighestChildLevel(node.allLeafChildren) : !!node.group
  const key = isGroup ? node.key : node.parent ? node.parent.key : ''
  const label = key ? getLabelFromKey(key) : ''
  const allLeafChildren = node?.allLeafChildren ?? []

  const columnIcon =
    rowGroupColId &&
    columnIcons?.[rowGroupColId] &&
    (isFunction(columnIcons[rowGroupColId])
      ? (columnIcons[rowGroupColId] as ColumnIconGetter)(label, allLeafChildren, props)
      : columnIcons[rowGroupColId])

  const labelRenderer =
    rowGroupColId && columnLabelRenderers?.[rowGroupColId] ? (
      columnLabelRenderers[rowGroupColId](label, allLeafChildren)
    ) : (
      <LabelWrapper data-test-id={`GroupCellRenderer-${label}`}>{label}</LabelWrapper>
    )

  return (
    <Container nestingLevel={node.level} isGroup={isGroup}>
      {isGroup && (
        <>
          <Tooltip tooltip={isGroup ? Locale.translate(Translation.SC_EXPAND_COLLAPSE_ROW) : ''}>
            <GroupIcon
              data-test-id={node.expanded ? `Collapse ${label}` : `Expand ${label}`}
              isExpanded={node.expanded}
              onClick={isGroup ? handleClick : noop}>
              {chevronCircleRight}
            </GroupIcon>
          </Tooltip>
          {labelRenderer}
          {columnIcon}
        </>
      )}
    </Container>
  )
}
